import {
  AlertOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
  BellOutlined,
  CarOutlined,
  CheckCircleFilled,
  DatabaseOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  InfoCircleFilled,
  SafetyCertificateOutlined,
  SyncOutlined,
  TableOutlined,
  TeamOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { ALERT_FAMILIES, ALERT_FAMILY_LABELS, type AlertFamily, type Locale } from '@wm/domain';
import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Progress,
  Row,
  Skeleton,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  useAlertSummary,
  useDashboard,
  useExpeditions,
  usePickupRequests,
  useSiteSummary,
  useStock,
} from '../api/hooks';
import type { ChecklistStep, DashboardData, WasteFamily } from '../api/types';
import { BarList } from '../components/charts/BarList';
import {
  Meter,
  ShareBar,
  StackedColumns,
  type StackSeries,
} from '../components/charts/StackedColumns';
import { PageHeader } from '../components/PageHeader';
import { StatCard } from '../components/StatCard';
import { useSiteContext } from '../context/SiteContext';
import { formatDate, formatMoney, formatNumber } from '../format';
import { openNotifications } from '../layout/Notifications';
import { TargetsSection } from './DashboardTargets';
import { brand, neutral } from '../theme';

const stepLinks: Record<ChecklistStep, string> = {
  IDENTITY: '/settings/site',
  ORIGINS: '/settings/origins',
  WASTES: '/wastes',
  CONDITIONNEMENTS: '/conditionnements',
  PRESTATAIRES: '/prestataires',
  CAP: '/caps',
  ROUTES: '/wastes',
};

/** The waste families in the fixed order of the categorical slots (dataviz palette). */
const FAMILIES: WasteFamily[] = ['DD', 'DND', 'INERTE'];
const FAMILY_COLOR: Record<WasteFamily, string> = {
  DD: 'var(--series-1)',
  DND: 'var(--series-2)',
  INERTE: 'var(--series-3)',
};

interface Task {
  key: string;
  tone: 'danger' | 'warning' | 'info' | 'success';
  title: string;
  detail?: string;
  to?: string;
  onClick?: () => void;
}

type Summary = NonNullable<ReturnType<typeof useSiteSummary>['data']>;

const lastDay = (month: string) => dayjs(`${month}-01`).endOf('month').format('YYYY-MM-DD');
const percent = (v: number | null) =>
  v === null ? '—' : `${formatNumber(Math.round(v * 1000) / 10)} %`;

/**
 * The site dashboard (FR-PIL-01 to FR-PIL-03): today's operations, the figures of the year from
 * the registre, the monthly trend and the largest wastes, what needs action, the open alerts by
 * family and, while the site is being set up, the onboarding checklist. Every figure opens the
 * list it was computed from.
 */
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { site, siteId, me } = useSiteContext();
  const summary = useSiteSummary(siteId);
  const dashboard = useDashboard(siteId);
  const alerts = useAlertSummary(siteId);
  const expeditions = useExpeditions(siteId);
  const requests = usePickupRequests(siteId);
  const stock = useStock(siteId);
  const lang: Locale = i18n.language.startsWith('ar')
    ? 'ar'
    : i18n.language.startsWith('en')
      ? 'en'
      : 'fr';

  if (!summary.data || !dashboard.data) return <Skeleton active paragraph={{ rows: 12 }} />;
  const s = summary.data;
  const d = dashboard.data;

  const exps = expeditions.data ?? [];
  const awaiting = exps.filter(
    (e) => e.status === 'AWAITING_SHIPMENT' || e.status === 'SIGNED_BY_PRODUCER',
  );
  const nextPickup = awaiting.map((e) => e.plannedDate).sort()[0];
  // as on the returns page: a forwarded BSD's copy comes back through its new BSD
  const returns = exps.filter(
    (e) =>
      (e.traceabilityStatus === 'PENDING' || e.traceabilityStatus === 'OVERDUE') &&
      ['SHIPPED', 'TEMP_STORED', 'RECEIVED'].includes(e.status),
  );
  const overdue = returns.filter((e) => e.traceabilityStatus === 'OVERDUE');
  const openRequests = (requests.data ?? []).filter(
    (r) => r.status === 'SUBMITTED' || r.status === 'ACCEPTED',
  );
  const balances = (stock.data ?? []).filter((b) => b.balanceT > 0);
  const stockTotal = balances.reduce((sum, b) => sum + b.balanceT, 0);
  const oldestDays = balances.reduce<number | null>(
    (m, b) => (b.ageDays !== null && (m === null || b.ageDays > m) ? b.ageDays : m),
    null,
  );

  const yearFrom = `${d.year}-01-01`;
  const registre = (extra: Record<string, string>) =>
    `/registre?${new URLSearchParams({ from: yearFrom, to: d.today, ...extra })}`;
  const shippedMovements = 'SHIPPED,CORRECTION';
  const series: StackSeries<WasteFamily>[] = FAMILIES.map((f) => ({
    key: f,
    label: t(`dashboard.families.${f}`),
    color: FAMILY_COLOR[f],
  }));

  const tasks = buildTasks(d, s, t);
  const open = tasks.filter((x) => x.tone !== 'success').length;
  const name = me?.principal.displayName?.split(' ')[0] ?? '';
  return (
    <>
      <PageHeader
        title={t('dashboard.greeting', { name })}
        subtitle={t('dashboard.subtitle', { site: site?.name })}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<CarOutlined />}
            label={t('dashboard.ops.awaiting')}
            value={awaiting.length}
            hint={
              nextPickup
                ? t('dashboard.ops.awaitingHint', { date: formatDate(nextPickup) })
                : t('dashboard.ops.nothingPlanned')
            }
            to="/expeditions?status=awaiting"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<SyncOutlined />}
            label={t('dashboard.ops.returns')}
            value={returns.length}
            hint={t('dashboard.ops.returnsHint', { count: overdue.length })}
            tone={overdue.length > 0 ? 'danger' : 'default'}
            to={overdue.length > 0 ? '/returns' : '/returns?scope=pending'}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<AppstoreOutlined />}
            label={t('dashboard.ops.requests')}
            value={openRequests.length}
            hint={t('dashboard.ops.requestsHint', {
              count: openRequests.filter((r) => r.status === 'SUBMITTED').length,
            })}
            tone={openRequests.length > 0 ? 'warning' : 'default'}
            to="/pickup-requests"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<DatabaseOutlined />}
            label={t('dashboard.ops.stock')}
            value={`${formatNumber(stockTotal)} t`}
            hint={
              oldestDays !== null ? t('dashboard.ops.stockHint', { count: oldestDays }) : undefined
            }
            to="/stock"
          />
        </Col>
      </Row>

      <div className="wm-section-title">{t('dashboard.year.title', { year: d.year })}</div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12} xl={6}>
          <Card style={{ height: '100%' }} styles={{ body: { padding: 18 } }}>
            <Typography.Text type="secondary" style={{ fontSize: 14 }}>
              {t('dashboard.year.tonnage')}
            </Typography.Text>
            <Link to={registre({ movement: shippedMovements })} style={{ color: 'inherit' }}>
              <div className="wm-figure">{formatNumber(d.tonnage.totalT)} t</div>
            </Link>
            <div style={{ margin: '10px 0' }}>
              <ShareBar
                series={series}
                values={d.tonnage.byType}
                label={t('dashboard.year.tonnageShare')}
              />
            </div>
            <Flex vertical gap={2}>
              {series.map((f) => (
                <Link
                  key={f.key}
                  to={registre({ category: f.key, movement: shippedMovements })}
                  className="wm-legend-row"
                >
                  <span className="wm-viz-swatch" style={{ background: f.color }} />
                  <span style={{ flex: 1 }}>{f.label}</span>
                  <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(d.tonnage.byType[f.key])} t
                  </strong>
                </Link>
              ))}
            </Flex>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={d.cost ? 5 : 6}>
          <FigureCard
            label={t('dashboard.year.valorisation')}
            value={percent(d.valorisation.rate)}
            to={registre({ movement: 'TREATED' })}
            meter={d.valorisation.rate}
            hint={
              d.valorisation.treatedT > 0
                ? t('dashboard.year.valorisationHint', {
                    recovered: formatNumber(d.valorisation.recoveredT),
                    treated: formatNumber(d.valorisation.treatedT),
                  })
                : t('dashboard.year.nothingTreated')
            }
          />
        </Col>
        {d.cost && (
          <Col xs={24} md={12} xl={5}>
            <FigureCard
              label={t('dashboard.year.cost')}
              value={formatMoney(d.cost.ytd)}
              to="/expeditions"
              hint={
                d.cost.perTonne !== null
                  ? t('dashboard.year.costPerTonne', { amount: formatMoney(d.cost.perTonne) })
                  : t('dashboard.year.noCost')
              }
            />
          </Col>
        )}
        <Col xs={24} md={12} xl={d.cost ? 4 : 6}>
          <FigureCard
            label={t('dashboard.year.expeditions')}
            value={String(d.expeditions.thisMonth)}
            to={`/registre?${new URLSearchParams({ from: `${d.month}-01`, to: d.today, movement: 'SHIPPED' })}`}
            hint={t('dashboard.year.lastMonth', { count: d.expeditions.lastMonth })}
          />
        </Col>
        <Col xs={24} md={12} xl={d.cost ? 4 : 6}>
          <FigureCard
            label={t('dashboard.year.checklist')}
            value={percent(d.checklist.score)}
            to="/expeditions?view=chargements"
            meter={d.checklist.score}
            hint={
              d.checklist.chargements
                ? t('dashboard.year.checklistHint', {
                    compliant: d.checklist.compliant,
                    count: d.checklist.chargements,
                  })
                : t('dashboard.year.noPickup')
            }
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={16}>
          <MonthlyCard
            d={d}
            series={series}
            registreFor={(m) =>
              `/registre?${new URLSearchParams({ from: `${m}-01`, to: lastDay(m), movement: shippedMovements })}`
            }
          />
        </Col>
        <Col xs={24} xl={8}>
          <Card title={t('dashboard.top.title', { year: d.year })} style={{ height: '100%' }}>
            {d.topWastes.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashboard.top.empty')} />
            ) : (
              <BarList
                unit="t"
                total={d.tonnage.totalT}
                items={d.topWastes.map((w) => ({
                  key: w.wasteId,
                  label: w.name,
                  sublabel: w.cmdCode,
                  value: w.shippedT,
                  to: registre({ wasteId: w.wasteId, movement: shippedMovements }),
                }))}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card
            data-tour="tasks"
            title={t('dashboard.tasks.title')}
            extra={open > 0 && <Tag>{open}</Tag>}
            style={{ height: '100%' }}
          >
            <TaskList tasks={tasks} />
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card title={t('dashboard.alerts.title')} style={{ marginBottom: 16 }}>
            <Flex vertical>
              {ALERT_FAMILIES.map((f: AlertFamily, i) => {
                const n = alerts.data?.byFamily[f] ?? 0;
                return (
                  <button
                    key={f}
                    type="button"
                    className="wm-family-count"
                    style={{ borderTop: i ? `1px solid ${brand.border}` : 'none' }}
                    onClick={() => openNotifications(f)}
                  >
                    <BellOutlined style={{ color: n ? brand.warning : brand.textMuted }} />
                    <span style={{ flex: 1, textAlign: 'start' }}>
                      {ALERT_FAMILY_LABELS[f][lang]}
                    </span>
                    <strong style={{ color: n ? brand.text : brand.textMuted }}>{n}</strong>
                  </button>
                );
              })}
            </Flex>
          </Card>
          {s.progress < 100 && <OnboardingCard s={s} />}
        </Col>
      </Row>

      <div className="wm-section-title" style={{ marginTop: 24 }}>
        {t('dashboard.setup')}
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            label={t('dashboard.kpi.wastes')}
            value={s.wastes.active}
            hint={t('dashboard.kpi.wastesHint', {
              hazardous: s.wastes.hazardous,
              adr: s.wastes.adr,
            })}
            icon={<DeleteOutlined />}
            to="/wastes"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            label={t('dashboard.kpi.caps')}
            value={
              <span>
                {s.caps.valid + s.caps.expiring}
                <Typography.Text type="secondary" style={{ fontSize: 16, fontWeight: 400 }}>
                  {' '}
                  / {s.caps.total - s.caps.replaced}
                </Typography.Text>
              </span>
            }
            hint={t('dashboard.kpi.capsHint', {
              expiring: s.caps.expiring,
              expired: s.caps.expired,
              draft: s.caps.draft,
            })}
            icon={<SafetyCertificateOutlined />}
            tone={s.caps.expired > 0 ? 'danger' : s.caps.expiring > 0 ? 'warning' : 'success'}
            to="/caps"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            label={t('dashboard.kpi.prestataires')}
            value={s.prestataires}
            hint={t('dashboard.kpi.prestatairesHint', { count: s.conditionnements })}
            icon={<TeamOutlined />}
            to="/prestataires"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            label={t('dashboard.kpi.authorizations')}
            value={s.authorizations.valid + s.authorizations.expiring}
            hint={t('dashboard.kpi.authorizationsHint', {
              expiring: s.authorizations.expiring,
              expired: s.authorizations.expired,
            })}
            icon={<AlertOutlined />}
            tone={
              s.authorizations.expired > 0
                ? 'danger'
                : s.authorizations.expiring > 0
                  ? 'warning'
                  : 'success'
            }
            to="/prestataires"
          />
        </Col>
      </Row>
      <div className="wm-section-title" style={{ marginTop: 24 }}>
        {t('dashboard.quality.title')}
      </div>
      <Row gutter={[16, 16]}>
        {(
          [
            ['estimated', registre({ movement: shippedMovements })],
            ['withoutReturn', '/returns?scope=pending'],
            ['wastesWithoutCap', '/compliance'],
          ] as const
        ).map(([k, to]) => {
          const q = d.quality[k];
          const share = q.total ? q.count / q.total : null;
          return (
            <Col xs={24} md={8} key={k}>
              <FigureCard
                label={t(`dashboard.quality.${k}`)}
                value={share === null ? '—' : percent(share)}
                to={to}
                meter={share}
                hint={t('dashboard.quality.of', { count: q.count, total: q.total })}
              />
            </Col>
          );
        })}
      </Row>
      <TargetsSection />
      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 16, fontSize: 12 }}>
        {t('dashboard.asOf', { date: formatDate(d.today) })}
      </Typography.Text>
    </>
  );
}

/** A figure of the year: its value, an optional meter (a ratio against 100 %) and a hint. */
function FigureCard({
  label,
  value,
  hint,
  to,
  meter,
}: {
  label: string;
  value: string;
  hint: string;
  to: string;
  meter?: number | null;
}) {
  return (
    <Link to={to} style={{ color: 'inherit', display: 'block', height: '100%' }}>
      <Card hoverable style={{ height: '100%' }} styles={{ body: { padding: 18 } }}>
        <Typography.Text type="secondary" style={{ fontSize: 14 }}>
          {label}
        </Typography.Text>
        <div className="wm-figure">{value}</div>
        {meter !== undefined && (
          <div style={{ margin: '10px 0 8px' }}>
            <Meter value={meter} label={`${label} : ${value}`} />
          </div>
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {hint}
        </Typography.Text>
      </Card>
    </Link>
  );
}

/** Shipped tonnage per month by family over twelve months, with its table view. */
function MonthlyCard({
  d,
  series,
  registreFor,
}: {
  d: DashboardData;
  series: StackSeries<WasteFamily>[];
  registreFor: (month: string) => string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [table, setTable] = useState(false);
  const columns = d.monthly.map((m) => ({
    label: dayjs(`${m.month}-01`).format('MMM'),
    title: dayjs(`${m.month}-01`).format('MMMM YYYY'),
    values: { DD: m.DD, DND: m.DND, INERTE: m.INERTE },
  }));
  const any = d.monthly.some((m) => m.DD || m.DND || m.INERTE);
  type Month = DashboardData['monthly'][number];
  return (
    <Card
      title={t('dashboard.monthly.title')}
      style={{ height: '100%' }}
      extra={
        any && (
          <Button
            size="small"
            type="text"
            icon={<TableOutlined />}
            onClick={() => setTable(!table)}
          >
            {table ? t('charts.showChart') : t('charts.showTable')}
          </Button>
        )
      }
    >
      {!any ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashboard.monthly.empty')} />
      ) : (
        <>
          <Flex gap={16} wrap style={{ marginBottom: 12 }}>
            {series.map((f) => (
              <Flex key={f.key} gap={6} align="center">
                <span className="wm-viz-swatch" style={{ background: f.color }} />
                <Typography.Text style={{ fontSize: 12 }}>{f.label}</Typography.Text>
              </Flex>
            ))}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('dashboard.monthly.unit')}
            </Typography.Text>
          </Flex>
          {table ? (
            <Table<Month>
              size="small"
              rowKey="month"
              pagination={false}
              scroll={{ x: 460 }}
              dataSource={d.monthly}
              onRow={(m) => ({ onClick: () => navigate(registreFor(m.month)) })}
              rowClassName="wm-clickable-row"
              columns={[
                {
                  title: t('dashboard.monthly.month'),
                  render: (_, m) => dayjs(`${m.month}-01`).format('MMMM YYYY'),
                },
                ...series.map((f) => ({
                  title: f.label,
                  align: 'end' as const,
                  render: (_: unknown, m: Month) => formatNumber(m[f.key]),
                })),
                {
                  title: t('charts.total'),
                  align: 'end' as const,
                  render: (_: unknown, m: Month) => (
                    <strong>
                      {formatNumber(Math.round((m.DD + m.DND + m.INERTE) * 1000) / 1000)}
                    </strong>
                  ),
                },
              ]}
            />
          ) : (
            <StackedColumns
              series={series}
              columns={columns}
              unit="t"
              ariaLabel={t('dashboard.monthly.title')}
              onSelect={(i) => navigate(registreFor(d.monthly[i]!.month))}
            />
          )}
        </>
      )}
    </Card>
  );
}

/** What needs action (FR-PIL-02), most urgent first; each task opens its list. */
function buildTasks(
  d: DashboardData,
  s: Summary,
  t: ReturnType<typeof useTranslation>['t'],
): Task[] {
  const k = d.tasks;
  const list: Task[] = [];
  const add = (cond: boolean, task: Task) => {
    if (cond) list.push(task);
  };
  add(k.overdueReturns > 0, {
    key: 'returns',
    tone: 'danger',
    title: t('dashboard.tasks.overdueReturns', { count: k.overdueReturns }),
    to: '/returns',
  });
  add(k.refusalsPending > 0, {
    key: 'refusals',
    tone: 'danger',
    title: t('dashboard.tasks.refusals', { count: k.refusalsPending }),
    to: '/expeditions?status=refusals',
  });
  add(k.capsExpired > 0, {
    key: 'caps-expired',
    tone: 'danger',
    title: t('dashboard.tasks.capsExpired', { count: k.capsExpired }),
    to: '/caps?status=EXPIRED',
  });
  add(s.wastes.hazardousWithoutUsableCap > 0, {
    key: 'no-cap',
    tone: 'danger',
    title: t('dashboard.attention.hazardousWithoutCap', {
      count: s.wastes.hazardousWithoutUsableCap,
    }),
    to: '/wastes?filter=noCap',
  });
  add(k.authorizationsExpired > 0, {
    key: 'auth-expired',
    tone: 'danger',
    title: t('dashboard.tasks.authorizationsExpired', { count: k.authorizationsExpired }),
    to: '/prestataires',
  });
  const report = k.annualReport;
  // the report of the past year matters from January until it is filed
  if (report.status !== 'FILED' && (report.late || d.today.slice(5) <= '03-31')) {
    list.push({
      key: 'annual-report',
      tone: report.late ? 'danger' : 'warning',
      title: t(`dashboard.tasks.report.${report.status}`, { year: report.year }),
      detail: t(report.late ? 'dashboard.tasks.report.late' : 'dashboard.tasks.report.due', {
        date: formatDate(report.deadline),
      }),
      to: '/annual-report',
    });
  }
  add(k.capsExpiring > 0, {
    key: 'caps-expiring',
    tone: 'warning',
    title: t('dashboard.tasks.capsExpiring', { count: k.capsExpiring }),
    to: '/caps?status=EXPIRING',
  });
  add(k.authorizationsExpiring > 0, {
    key: 'auth-expiring',
    tone: 'warning',
    title: t('dashboard.tasks.authorizationsExpiring', { count: k.authorizationsExpiring }),
    to: '/prestataires',
  });
  add(k.requestsToDecide > 0, {
    key: 'requests-decide',
    tone: 'warning',
    title: t('dashboard.tasks.requestsToDecide', { count: k.requestsToDecide }),
    to: '/pickup-requests',
  });
  add(k.requestsToPlan > 0, {
    key: 'requests-plan',
    tone: 'info',
    title: t('dashboard.tasks.requestsToPlan', { count: k.requestsToPlan }),
    to: '/pickup-requests',
  });
  add(k.exemptionsToReview > 0, {
    key: 'exemptions',
    tone: 'info',
    title: t('dashboard.tasks.exemptions', { count: k.exemptionsToReview }),
    onClick: () => openNotifications('EVENT'),
  });
  add(s.wastes.withoutDefaultRoute > 0, {
    key: 'no-route',
    tone: 'info',
    title: t('dashboard.attention.withoutRoute', { count: s.wastes.withoutDefaultRoute }),
    to: '/wastes?filter=noRoute',
  });
  add(report.status === 'FILED', {
    key: 'annual-report-filed',
    tone: 'success',
    title: t('dashboard.tasks.report.FILED', { year: report.year }),
    to: '/annual-report',
  });
  return list;
}

const TONE_ICON = {
  danger: <WarningFilled style={{ color: brand.danger, fontSize: 16 }} />,
  warning: <ExclamationCircleFilled style={{ color: brand.warning, fontSize: 16 }} />,
  info: <InfoCircleFilled style={{ color: brand.info, fontSize: 16 }} />,
  success: <CheckCircleFilled style={{ color: brand.success, fontSize: 16 }} />,
};

function TaskList({ tasks }: { tasks: Task[] }) {
  const { t } = useTranslation();
  const open = tasks.filter((x) => x.tone !== 'success');
  return (
    <Flex vertical>
      {open.length === 0 && (
        <Flex align="center" gap={10} style={{ padding: '12px 0' }}>
          <CheckCircleFilled style={{ color: brand.success, fontSize: 20 }} />
          <Typography.Text>{t('dashboard.attention.allGood')}</Typography.Text>
        </Flex>
      )}
      {tasks.map((x, i) => (
        <TaskRow key={x.key} task={x} first={i === 0 && open.length > 0} />
      ))}
    </Flex>
  );
}

function TaskRow({ task, first }: { task: Task; first: boolean }) {
  const content = (
    <Flex
      align="center"
      gap={12}
      style={{ padding: '10px 4px', borderTop: first ? 'none' : `1px solid ${brand.border}` }}
    >
      {TONE_ICON[task.tone]}
      <div style={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
        <div style={{ color: brand.text }}>{task.title}</div>
        {task.detail && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {task.detail}
          </Typography.Text>
        )}
      </div>
      <ArrowRightOutlined style={{ color: brand.textMuted }} className="wm-flip-rtl" />
    </Flex>
  );
  return task.to ? (
    <Link to={task.to}>{content}</Link>
  ) : (
    <button type="button" className="wm-task-button" onClick={task.onClick}>
      {content}
    </button>
  );
}

function OnboardingCard({ s }: { s: Summary }) {
  const { t } = useTranslation();
  return (
    <Card
      title={t('dashboard.onboarding.title')}
      extra={<Tag color="processing">{s.progress} %</Tag>}
    >
      <Flex gap={20} align="center" style={{ marginBottom: 16 }}>
        <Progress type="circle" percent={s.progress} size={72} strokeColor={brand.primary} />
        <Typography.Text type="secondary">{t('dashboard.onboarding.hint')}</Typography.Text>
      </Flex>
      <Flex vertical gap={4}>
        {s.checklist.map((c) => (
          <Link key={c.step} to={stepLinks[c.step]}>
            <Flex
              align="center"
              gap={12}
              style={{
                padding: '9px 10px',
                borderRadius: 8,
                background: c.done ? 'transparent' : neutral.background2,
                border: c.done ? '1px solid transparent' : `1px solid ${brand.border}`,
              }}
            >
              {c.done ? (
                <CheckCircleFilled style={{ color: brand.success, fontSize: 18 }} />
              ) : (
                <ExclamationCircleFilled style={{ color: brand.warning, fontSize: 18 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 550, color: brand.text }}>
                  {t(`dashboard.steps.${c.step}.title`)}
                </div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {c.done
                    ? t(`dashboard.steps.${c.step}.done`, { count: c.count ?? 0 })
                    : (c.step === 'CAP' || c.step === 'ROUTES') && s.wastes.active === 0
                      ? t('dashboard.steps.waitingWastes')
                      : t(`dashboard.steps.${c.step}.todo`, { count: c.count ?? 0 })}
                </Typography.Text>
              </div>
              <ArrowRightOutlined style={{ color: brand.textMuted }} className="wm-flip-rtl" />
            </Flex>
          </Link>
        ))}
      </Flex>
    </Card>
  );
}
